/**
 * Plausible Analytics Event Tracking Helpers
 *
 * Plausible is cookieless and GDPR-compliant without consent banners.
 * The script is loaded directly in app.blade.php (outside GTM).
 */

import {
    buildPurchaseDedupeKey,
    runTrackedEventOnce,
} from '@/utils/trackingEventHelpers';

declare global {
    interface Window {
        plausible?: (
            eventName: string,
            options?: {
                revenue?: { currency: string; amount: number };
                props?: Record<string, string | number | boolean>;
                callback?: () => void;
            }
        ) => void;
    }
}

const track = (
    eventName: string,
    options?: {
        revenue?: { currency: string; amount: number };
        props?: Record<string, string | number | boolean>;
    },
): void => {
    if (typeof window === 'undefined' || typeof window.plausible !== 'function') {
        return;
    }

    window.plausible(eventName, options);
};

export const trackPlausibleAddToCart = (params: {
    amount: number;
    type: 'physical' | 'digital';
}): void => {
    track('AddToCart', {
        props: {
            voucher_type: params.type,
            voucher_amount: params.amount,
        },
    });
};

export const trackPlausibleViewCart = (params: {
    total: number;
    itemCount: number;
    items?: Array<{ type: string; amount: number; quantity?: number; background?: string }>;
}): void => {
    track('ViewCart', {
        props: {
            item_count: params.itemCount,
            cart_value: params.total,
        },
    });
};

export const trackPlausibleBeginCheckout = (params: {
    total: number;
    itemCount: number;
    paymentIntentId?: string;
}): void => {
    track('BeginCheckout', {
        props: {
            item_count: params.itemCount,
            cart_value: params.total,
        },
    });
};

export const trackPlausiblePurchaseConfirmed = (transactionId: string): void => {
    runTrackedEventOnce(`${buildPurchaseDedupeKey(transactionId)}:plausible_confirmed`, 'local', () => {
        track('PurchaseConfirmed');
    });
};

export const trackPlausiblePurchase = (params: {
    total: number;
    orderNumber: string;
    voucherType: 'physical' | 'digital' | 'mixed';
    promotionUsed: boolean;
    itemCount: number;
    paymentMethod: string;
}): void => {
    track('Purchase', {
        revenue: { currency: 'EUR', amount: params.total },
        props: {
            order_number: params.orderNumber,
            voucher_type: params.voucherType,
            promotion_used: params.promotionUsed,
            item_count: params.itemCount,
            payment_method: params.paymentMethod,
        },
    });
};
